import { useState, useMemo, useEffect } from 'react';

/**
 * Custom hook to handle pagination logic.
 *
 * @param {Array} normalizedData - The normalized array of data that needs to be paginated.
 * @param {Number} recordsPerPage - The number of records to display per page.
 * @param {Function} onPageChange - Callback function triggered when the page changes.
 *
 * @returns {Object} - An object containing:
 * - {number} currentPage: The current page number.
 * - {Array} currentRecords: The records to display for the current page.
 * - {number} totalPages: The total number of pages.
 * - {Array} getPaginatedPageNumbers: An array of numbers and ellipsis (...) representing the paginated page structure.
 * - {Function} handlePageChange: Function to jump to a specific page.
 * - {Function} goToPrevPage: Function to navigate to the previous page.
 * - {Function} goToNextPage: Function to navigate to the next page.
 * - {Function} resetPagination: Function to reset paginator back to page 1.
 *
 * Internal Utility:
 * - getRange(start: number, end: number): Array<number> - Generates an array of sequential numbers
 *   between the specified start and end values, inclusive. Used for creating page ranges.
 */
const usePagination = (normalizedData, recordsPerPage, totalItems, selectedDate) => {
  const [currentPage, setCurrentPage] = useState(1);

  const getRange = (start, end) => Array.from({ length: end - start + 1 }, (_, i) => start + i);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalItems / recordsPerPage);
  }, [recordsPerPage, totalItems]);

  const getPaginatedPageNumbers = useMemo(() => {
    const maxPagesToShow = 5;

    if (totalPages <= 1) return [1];
    if (totalPages <= maxPagesToShow) return getRange(1, totalPages);

    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    const pages = [];
    if (startPage > 1) pages.push(1, '...');
    pages.push(...getRange(startPage, endPage));
    if (endPage < totalPages) pages.push('...', totalPages);

    return pages;
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    if (page !== '...' && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const resetPagination = () => {
    setCurrentPage(1);
  };

  return {
    currentPage,
    currentRecords: normalizedData,
    totalPages,
    getPaginatedPageNumbers,
    handlePageChange,
    goToPrevPage,
    goToNextPage,
    resetPagination,
  };
};

export default usePagination;
